<template>
  <div class="exact-modal">
    <div ref="scrollArea" class="exact-modal-body">
      <i class="icon icon--dialog-document"/>
      <h1 class="title">{{ $t('referral.create.title') }}</h1>
      <p class="referral-code">{{ referralCode }}</p>
      <p class="description" v-html="$t('referral.create.description')"></p>
      <template v-if="isReferralTermRequired">
        <div class="term">
          <p class="term-title">
            <a class="link" :href="referralTermLink" target="_blank">{{ $t('referral.create.term.title') }}</a>
            <span class="necessary">{{ $t('referral.create.term.sub') }}</span>
          </p>
          <ul class="term-list">
            <li v-html="$t('referral.create.term.1')"></li>
            <li v-html="$t('referral.create.term.2')"></li>
            <li v-html="$t('referral.create.term.3')"></li>
          </ul>
        </div>
      </template>
      <hr class="border">
    </div>
    <div class="exact-modal-footer" :class="{'has-scroll': hasScroll}">
      <template v-if="isReferralTermRequired">
        <ui-checkbox :value="checkConfirm" @input="onChangeCheckboxValue">
          {{ $t('referral.termConfirm') }}
        </ui-checkbox>
      </template>
      <div>
        <button class="btf-button outline" @click="$emit('close')">
          {{ $t('common.cancel') }}
        </button>
        <button
          class="btf-button primary outline"
          :class="{ disabled: !checkConfirm && isReferralTermRequired }"
          @click="$emit('confirm')"
        >
          {{ $t('common.confirm') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import UiCheckbox from '@/components/ui/UiCheckbox.vue'
import { mapState } from 'vuex'
import { checkReferExist } from '@/api/referral'

export default {
  name: 'ReferralConfirm',
  components: { UiCheckbox },
  props: {
    referralCode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      checkConfirm: false,
      hasScroll: false,
      heatmapReferralCode: null,
      isReferralTermRequired: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ]),
    referralTermLink () {
      return `${process.env.VUE_APP_HOME_URL}/${this.$i18n.locale}/referralTerms`
    }
  },
  async created () {
    await checkReferExist({ userId: this.userData.userId, serviceType: 'beusable' })
      .then(res => {
        this.heatmapReferralCode = res.referral
        this.isReferralTermRequired = !res.terms
      })
      .catch(e => {
        console.error(e)
      })
  },
  async mounted () {
    this.checkScroll()
    window.addEventListener('resize', this.checkScroll)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkScroll)
  },
  methods: {
    onChangeCheckboxValue (value) {
      this.checkConfirm = value
    },
    checkScroll () {
      this.$nextTick(() => {
        const scrollArea = this.$refs.scrollArea
        this.hasScroll = scrollArea.scrollHeight > scrollArea.clientHeight
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.exact-modal {
  display: flex;
  flex-direction: column;
  max-height: calc(100% - 144px);
  min-height: 360px;
  width: 530px;
  padding: 28px 0 24px;
  border-radius: 14px;
  background-color: #fff;
  font-weight: 400;
  color: #2f2f2f;
  box-sizing: border-box;
  &-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 19px 28px 0;
    overflow-y: auto;
    .title {
      margin-bottom: 12px;
      font-weight: 600;
      font-size: 20px;
      line-height: 120%;
      color: #444;
    }
    .referral-code {
      font-weight: 600;
      font-size: 20px;
      line-height: 120%;
      color: #0E90A5;
    }
    .description {
      margin: 12px 0;
      font-size: 14px;
      line-height: 150%;
      color: #2f2f2f;
      text-align: center;
    }
    .term {
      width: 100%;
      padding: 20px;
      border-radius: 5px;
      border: 1px solid #d7d7d7;
      background-color: #f4f4f4;
      box-sizing: border-box;
      &-title {
        margin-bottom: 8px;
        font-weight: 600;
        font-size: 12px;
        line-height: 150%;
        .link {
          text-decoration: underline;
          color: #444;
        }
        .necessary {
          font-weight: 400;
          color: $brandColor;
        }
      }
      &-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        color: #444;
        font-size: 12px;
        line-height: 1.5;
        list-style-type: disc;
        padding-left: 20px;
      }
    }
    .border {
      flex-shrink: 0;
      height: 1px;
      margin: 0;
      border: none;
      background: #d7d7d7;
    }
  }
  &-footer {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 42px;
    padding: 12px 28px 0;

    & /deep/ .ui_checkbox {
      margin-left: 0;
      .text {
        color: #2f2f2f;
        font-size: 13px;
        font-weight: 400;
      }
    }
    &.has-scroll {
      &:before {
        content: '';
        position: absolute;
        width: 100%;
        height: 12px;
        left: 0;
        bottom: 100%;
        z-index: 100;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.06) 100%);
      }
    }
    .btf-button {
      &.disabled {
        opacity: .33;
        filter: grayscale(0);
      }
    }
  }
}
</style>
